/**
 * Sansegynge theme main entry point.
 *
 * @package Sansegynge
 */

// Self-hosted fonts via Fontsource (GDPR -- no external CDN requests)
import '@fontsource-variable/cormorant-garamond';
import '@fontsource-variable/cormorant-garamond/wght-italic.css';
import '@fontsource-variable/dm-sans';

// Import styles -- Vite processes CSS imports for WordPress enqueuing.
import '../css/main.css';

// WC notice auto-dismiss, close buttons, and AJAX re-initialization.
import { initNotices, observeNewNotices } from './notices.js';

console.log('[Sansegynge] Theme loaded.');

/**
 * Initialize site shell interactivity once DOM is ready.
 */
document.addEventListener('DOMContentLoaded', () => {
	initMobileMenu();
	initSearchOverlay();
	initMegaMenu();
	initStickyNav();
	initFooterAccordion();
	initScrollAnimations();
	initCustomSelects();
	initNotices();
	observeNewNotices();
	initB2bToggle();
	initFaqDeepLinks();
});

/**
 * Open the targeted FAQ accordion item when arriving via a hash link.
 *
 * Native <details> does not open on :target, so a visitor landing from a
 * search result, the FAQPage schema, or a shared deep link would otherwise
 * see a closed answer. Opens the matching item on load and on hashchange.
 */
function initFaqDeepLinks() {
	const openFromHash = () => {
		const hash = window.location.hash;
		if (!hash || hash.length < 2) {
			return;
		}

		let target;
		try {
			target = document.querySelector(hash);
		} catch (e) {
			return;
		}

		if (target && target.matches('details.sg-faq-item')) {
			target.open = true;
			target.scrollIntoView();
		}
	};

	openFromHash();
	window.addEventListener('hashchange', openFromHash);
}

/**
 * B2B-toggle på Min konto → Adresser.
 *
 * Lytter på #is_b2b checkboxen og toggler .sg-b2b-active på den omsluttende
 * formular. CSS skjuler/viser .sg-b2b-field og .sg-b2b-cvr-status baseret
 * på klassen. CVR-lookup ligger kun i checkout-page.js — Min konto har
 * manuel indtastning.
 */
function initB2bToggle() {
	const checkbox = document.getElementById('is_b2b');
	if (!checkbox) return;

	const form = checkbox.closest('form');
	if (!form) return;

	function syncState() {
		if (checkbox.checked) {
			form.classList.add('sg-b2b-active');
		} else {
			form.classList.remove('sg-b2b-active');
			// Ryd værdier på Min konto-formen ved un-check
			const company = form.querySelector('#billing_company');
			const cvr = form.querySelector('#billing_cvr');
			if (company) company.value = '';
			if (cvr) cvr.value = '';
		}
	}

	checkbox.addEventListener('change', syncState);

	// Initial state ved page load
	if (checkbox.checked) {
		form.classList.add('sg-b2b-active');
	}
}

/**
 * Mobile menu toggle behavior.
 *
 * Opens/closes the full-screen mobile menu overlay with slide-in
 * transition, body scroll lock, ARIA updates, and Escape key support.
 */
function initMobileMenu() {
	const toggle = document.querySelector('[data-menu-toggle]');
	const closeBtn = document.querySelector('[data-menu-close]');
	const menu = document.querySelector('[data-mobile-menu]');

	if (!toggle || !closeBtn || !menu) {
		return;
	}

	function openMenu() {
		menu.classList.remove('-translate-x-full', 'invisible');
		menu.classList.add('translate-x-0', 'visible');
		menu.setAttribute('aria-hidden', 'false');
		toggle.setAttribute('aria-expanded', 'true');
		document.body.style.overflow = 'hidden';
	}

	function closeMenu() {
		menu.classList.remove('translate-x-0', 'visible');
		menu.classList.add('-translate-x-full', 'invisible');
		menu.setAttribute('aria-hidden', 'true');
		toggle.setAttribute('aria-expanded', 'false');
		document.body.style.overflow = '';
	}

	toggle.addEventListener('click', openMenu);
	closeBtn.addEventListener('click', closeMenu);

	// Close on Escape key
	document.addEventListener('keydown', (e) => {
		if (e.key === 'Escape' && menu.getAttribute('aria-hidden') === 'false') {
			closeMenu();
		}
	});

	// Close when clicking a menu link (for single-page navigation)
	menu.querySelectorAll('a').forEach((link) => {
		link.addEventListener('click', closeMenu);
	});

	// Focus trap: keep Tab within mobile menu when open
	menu.addEventListener('keydown', (e) => {
		if (e.key !== 'Tab') {
			return;
		}

		const focusable = menu.querySelectorAll(
			'a[href], button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
		);
		const first = focusable[0];
		const last = focusable[focusable.length - 1];

		if (e.shiftKey && document.activeElement === first) {
			e.preventDefault();
			last.focus();
		} else if (!e.shiftKey && document.activeElement === last) {
			e.preventDefault();
			first.focus();
		}
	});
}

/**
 * Search overlay.
 *
 * The search icon is a real link to the results page, so without JavaScript
 * the old behaviour stands. With JavaScript it opens an overlay that offers
 * popular searches and the current bestsellers before anything is typed.
 */
function initSearchOverlay() {
	const overlay = document.querySelector('[data-search-overlay]');
	const openers = document.querySelectorAll('[data-search-open]');
	const closeBtn = overlay ? overlay.querySelector('[data-search-close]') : null;
	const input = overlay ? overlay.querySelector('[data-search-input]') : null;

	if (!overlay || !openers.length || !closeBtn || !input) {
		return;
	}

	let lastFocused = null;

	function openOverlay(e) {
		if (e) {
			e.preventDefault();
		}
		lastFocused = document.activeElement;
		overlay.classList.add('sg-search-overlay--open');
		overlay.setAttribute('aria-hidden', 'false');
		openers.forEach((o) => o.setAttribute('aria-expanded', 'true'));
		document.body.style.overflow = 'hidden';
		// Wait for the transition to start before focusing, or iOS skips the keyboard.
		window.setTimeout(() => input.focus(), 80);
	}

	function closeOverlay() {
		overlay.classList.remove('sg-search-overlay--open');
		overlay.setAttribute('aria-hidden', 'true');
		openers.forEach((o) => o.setAttribute('aria-expanded', 'false'));

		// The mobile menu may still be open underneath; it owns the scroll lock then.
		const mobileMenu = document.querySelector('[data-mobile-menu]');
		const menuOpen = mobileMenu && mobileMenu.getAttribute('aria-hidden') === 'false';
		document.body.style.overflow = menuOpen ? 'hidden' : '';

		if (lastFocused) {
			lastFocused.focus();
		}
	}

	openers.forEach((opener) => opener.addEventListener('click', openOverlay));
	closeBtn.addEventListener('click', closeOverlay);

	document.addEventListener('keydown', (e) => {
		if (e.key === 'Escape' && overlay.getAttribute('aria-hidden') === 'false') {
			closeOverlay();
		}
	});

	// Click outside the panel closes it.
	overlay.addEventListener('click', (e) => {
		if (e.target === overlay) {
			closeOverlay();
		}
	});

	// Keep Tab inside the overlay while it is open.
	overlay.addEventListener('keydown', (e) => {
		if (e.key !== 'Tab') {
			return;
		}

		const focusable = overlay.querySelectorAll(
			'a[href], button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
		);
		if (!focusable.length) {
			return;
		}
		const first = focusable[0];
		const last = focusable[focusable.length - 1];

		if (e.shiftKey && document.activeElement === first) {
			e.preventDefault();
			last.focus();
		} else if (!e.shiftKey && document.activeElement === last) {
			e.preventDefault();
			first.focus();
		}
	});
}

/**
 * Mega menu behavior.
 *
 * Desktop: hover-triggered dropdown panels positioned below the nav bar.
 * Uses mouseenter/mouseleave with a short delay to prevent flicker.
 * Mobile: accordion submenus toggled via chevron buttons.
 * Updates ARIA attributes for accessibility.
 */
function initMegaMenu() {
	// --- Desktop: hover panels ---
	const triggers = document.querySelectorAll('[data-mega-trigger]');
	const panels = document.querySelectorAll('[data-mega-panel]');

	if (!triggers.length || !panels.length) {
		return;
	}

	let closeTimer = null;

	function openPanel(slug) {
		clearTimeout(closeTimer);

		// Close any other open panels first.
		panels.forEach((p) => {
			if (p.getAttribute('data-mega-panel') !== slug) {
				p.classList.remove('mega-panel--open');
				p.setAttribute('aria-hidden', 'true');
			}
		});

		const panel = document.querySelector(`[data-mega-panel="${slug}"]`);
		const trigger = document.querySelector(`[data-mega-trigger="${slug}"]`);
		if (panel) {
			panel.classList.add('mega-panel--open');
			panel.setAttribute('aria-hidden', 'false');
		}
		if (trigger) {
			trigger.setAttribute('aria-expanded', 'true');
		}
	}

	function closePanel(slug) {
		closeTimer = setTimeout(() => {
			const panel = document.querySelector(`[data-mega-panel="${slug}"]`);
			const trigger = document.querySelector(`[data-mega-trigger="${slug}"]`);
			if (panel) {
				panel.classList.remove('mega-panel--open');
				panel.setAttribute('aria-hidden', 'true');
			}
			if (trigger) {
				trigger.setAttribute('aria-expanded', 'false');
			}
		}, 150);
	}

	function closeAllPanels() {
		clearTimeout(closeTimer);
		panels.forEach((p) => {
			p.classList.remove('mega-panel--open');
			p.setAttribute('aria-hidden', 'true');
		});
		triggers.forEach((t) => {
			t.setAttribute('aria-expanded', 'false');
		});
	}

	triggers.forEach((trigger) => {
		const slug = trigger.getAttribute('data-mega-trigger');

		// Hover: open on mouseenter the trigger link.
		trigger.addEventListener('mouseenter', () => openPanel(slug));
		trigger.addEventListener('mouseleave', () => closePanel(slug));

		// Click: toggle panel (also works as fallback for touch devices on desktop).
		trigger.addEventListener('click', (e) => {
			const panel = document.querySelector(`[data-mega-panel="${slug}"]`);
			if (panel && panel.classList.contains('mega-panel--open')) {
				// Already open: let click navigate to the link href.
				return;
			}
			e.preventDefault();
			openPanel(slug);
		});

		// Keyboard: Enter/Space toggles, Escape closes.
		trigger.addEventListener('keydown', (e) => {
			if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault();
				const panel = document.querySelector(`[data-mega-panel="${slug}"]`);
				if (panel && panel.classList.contains('mega-panel--open')) {
					closeAllPanels();
				} else {
					openPanel(slug);
				}
			}
			if (e.key === 'Escape') {
				closeAllPanels();
			}
		});
	});

	// Keep panel open when hovering over the panel itself.
	panels.forEach((panel) => {
		panel.addEventListener('mouseenter', () => clearTimeout(closeTimer));
		panel.addEventListener('mouseleave', () => {
			const slug = panel.getAttribute('data-mega-panel');
			closePanel(slug);
		});
	});

	// Close all panels when pressing Escape anywhere.
	document.addEventListener('keydown', (e) => {
		if (e.key === 'Escape') {
			closeAllPanels();
		}
	});

	// Close all panels when clicking outside nav area.
	document.addEventListener('click', (e) => {
		const nav = document.querySelector('[data-site-nav]');
		if (nav && !nav.contains(e.target)) {
			closeAllPanels();
		}
	});

	// --- Mobile: accordion toggles ---
	const mobileToggles = document.querySelectorAll('[data-mobile-mega-toggle]');

	mobileToggles.forEach((toggle) => {
		toggle.addEventListener('click', () => {
			const slug = toggle.getAttribute('data-mobile-mega-toggle');
			const content = document.querySelector(`[data-mobile-mega-content="${slug}"]`);

			if (!content) {
				return;
			}

			const isOpen = toggle.getAttribute('aria-expanded') === 'true';
			toggle.setAttribute('aria-expanded', String(!isOpen));
			content.setAttribute('aria-hidden', String(isOpen));

			if (isOpen) {
				content.style.maxHeight = '0';
				content.style.opacity = '0';
			} else {
				content.style.maxHeight = content.scrollHeight + 'px';
				content.style.opacity = '1';
			}
		});
	});
}

/**
 * Sticky navigation scroll behavior.
 *
 * Adds frosted-glass background with backdrop blur when the user scrolls
 * past the announcement bar. Restores transparent background at top.
 * Uses requestAnimationFrame throttling for scroll performance.
 */
function initStickyNav() {
	const nav = document.querySelector('[data-site-nav]');

	if (!nav) {
		return;
	}

	// Classes for the scrolled (frosted glass) state
	const scrolledClasses = ['bg-[rgba(250,248,245,0.95)]', 'backdrop-blur-[10px]', 'border-medium'];
	const defaultClasses = ['bg-transparent', 'border-subtle'];

	let ticking = false;
	let isScrolled = false;

	function onScroll() {
		if (ticking) {
			return;
		}

		ticking = true;
		requestAnimationFrame(() => {
			const scrollY = window.scrollY;

			if (scrollY > 0 && !isScrolled) {
				nav.classList.remove(...defaultClasses);
				nav.classList.add(...scrolledClasses);
				isScrolled = true;
			} else if (scrollY === 0 && isScrolled) {
				nav.classList.remove(...scrolledClasses);
				nav.classList.add(...defaultClasses);
				isScrolled = false;
			}

			ticking = false;
		});
	}

	window.addEventListener('scroll', onScroll, { passive: true });
}

/**
 * Footer accordion behavior for mobile.
 *
 * Toggles link column sections open/closed on mobile via max-height
 * animation. Desktop columns are always visible via CSS (md:max-h-none).
 * Updates aria-expanded and drives chevron rotation via CSS selector.
 */
function initFooterAccordion() {
	const accordionToggles = document.querySelectorAll('[data-accordion-toggle]');

	accordionToggles.forEach((toggle) => {
		toggle.addEventListener('click', () => {
			const content = toggle
				.closest('[data-accordion-section]')
				?.querySelector('[data-accordion-content]');

			if (!content) {
				return;
			}

			const isOpen = toggle.getAttribute('aria-expanded') === 'true';
			toggle.setAttribute('aria-expanded', String(!isOpen));

			if (isOpen) {
				content.style.maxHeight = '0';
			} else {
				content.style.maxHeight = content.scrollHeight + 'px';
			}
		});
	});
}

/**
 * Scroll-triggered entrance animations.
 *
 * Uses IntersectionObserver to reveal below-fold sections with fadeInUp
 * and stagger card grids with sequential delays. Each element animates
 * exactly once (observer.unobserve after trigger). Respects
 * prefers-reduced-motion by making all content immediately visible.
 */
function initScrollAnimations() {
	// Respect user preference for reduced motion
	if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
		document.querySelectorAll('.section--animate').forEach((el) => {
			el.classList.remove('section--animate');
		});
		document.querySelectorAll('[data-animate-stagger]').forEach((el) => {
			el.classList.add('stagger--visible');
		});
		return;
	}

	const animateEls = document.querySelectorAll('[data-animate], [data-animate-stagger]');

	if (!animateEls.length) {
		return;
	}

	const observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (!entry.isIntersecting) {
					return;
				}

				if (entry.target.hasAttribute('data-animate')) {
					entry.target.classList.add('section--visible');
				}

				if (entry.target.hasAttribute('data-animate-stagger')) {
					entry.target.classList.add('stagger--visible');
				}

				observer.unobserve(entry.target);
			});
		},
		{
			threshold: 0.1,
			rootMargin: '0px 0px -100px 0px',
		}
	);

	animateEls.forEach((el) => {
		observer.observe(el);
	});
}

/**
 * Custom styled select dropdowns.
 *
 * Replaces native <select> elements with fully styled custom dropdowns
 * while keeping the hidden native select in sync for form submission.
 * Targets WooCommerce ordering selects on category/shop archives.
 */
function initCustomSelects() {
	const selects = document.querySelectorAll('.woocommerce-ordering .orderby');

	if (!selects.length) {
		return;
	}

	selects.forEach((nativeSelect) => {
		const wrapper = document.createElement('div');
		wrapper.className = 'custom-select';

		// Build trigger button showing current value
		const trigger = document.createElement('button');
		trigger.type = 'button';
		trigger.className = 'custom-select__trigger';
		trigger.setAttribute('aria-haspopup', 'listbox');
		trigger.setAttribute('aria-expanded', 'false');

		const selectedOption = nativeSelect.options[nativeSelect.selectedIndex];
		trigger.textContent = selectedOption ? selectedOption.text : '';

		// Build dropdown panel
		const dropdown = document.createElement('div');
		dropdown.className = 'custom-select__dropdown';
		dropdown.setAttribute('role', 'listbox');

		Array.from(nativeSelect.options).forEach((opt) => {
			const item = document.createElement('div');
			item.className = 'custom-select__option';
			item.setAttribute('role', 'option');
			item.setAttribute('data-value', opt.value);
			item.textContent = opt.text;

			if (opt.selected) {
				item.classList.add('custom-select__option--active');
				item.setAttribute('aria-selected', 'true');
			}

			item.addEventListener('click', () => {
				// Update native select and trigger WooCommerce's change handler
				nativeSelect.value = opt.value;
				nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));

				// Update visual state
				trigger.textContent = opt.text;
				dropdown.querySelectorAll('.custom-select__option').forEach((o) => {
					o.classList.remove('custom-select__option--active');
					o.setAttribute('aria-selected', 'false');
				});
				item.classList.add('custom-select__option--active');
				item.setAttribute('aria-selected', 'true');

				closeDropdown();
			});

			dropdown.appendChild(item);
		});

		// Toggle open/close
		function openDropdown() {
			wrapper.classList.add('custom-select--open');
			trigger.setAttribute('aria-expanded', 'true');
		}

		function closeDropdown() {
			wrapper.classList.remove('custom-select--open');
			trigger.setAttribute('aria-expanded', 'false');
		}

		trigger.addEventListener('click', (e) => {
			e.preventDefault();
			if (wrapper.classList.contains('custom-select--open')) {
				closeDropdown();
			} else {
				openDropdown();
			}
		});

		// Close on outside click
		document.addEventListener('click', (e) => {
			if (!wrapper.contains(e.target)) {
				closeDropdown();
			}
		});

		// Keyboard support
		trigger.addEventListener('keydown', (e) => {
			if (e.key === 'Escape') {
				closeDropdown();
			} else if (e.key === 'ArrowDown' || e.key === 'Enter') {
				e.preventDefault();
				openDropdown();
				const first = dropdown.querySelector('.custom-select__option');
				if (first) first.focus();
			}
		});

		dropdown.addEventListener('keydown', (e) => {
			if (e.key === 'Escape') {
				closeDropdown();
				trigger.focus();
			} else if (e.key === 'ArrowDown') {
				e.preventDefault();
				const next = document.activeElement.nextElementSibling;
				if (next) next.focus();
			} else if (e.key === 'ArrowUp') {
				e.preventDefault();
				const prev = document.activeElement.previousElementSibling;
				if (prev) prev.focus();
			} else if (e.key === 'Enter') {
				e.preventDefault();
				document.activeElement.click();
			}
		});

		// Make options focusable
		dropdown.querySelectorAll('.custom-select__option').forEach((opt) => {
			opt.setAttribute('tabindex', '-1');
		});

		// Hide native select, insert custom dropdown
		nativeSelect.style.display = 'none';
		wrapper.appendChild(trigger);
		wrapper.appendChild(dropdown);
		nativeSelect.parentNode.insertBefore(wrapper, nativeSelect);
	});
}
